import { Map } from 'immutable';
import { createReducer, SimpleActionCreator } from 'redux-act';

import { accountDeleted, accountDeletionFailed, deleteAccount } from '@/store/actions/authentication/deleteAccount';
import { accountReactivated, accountReactivationFailed, reactivateAccount } from '@/store/actions/authentication/reactivateAccount';
import { confirmReservation, reservationConfirmationFailed, reservationConfirmationFinished } from '@/store/actions/reservation/confirmReservation';

import PostState, { completePost, failPost, post } from '../../utils/PostState';
import { authenticationRequired } from '../actions/authentication/authenticationRequired';
import { login, loginFailed, loginSuccessful } from '../actions/authentication/login';
import { logout } from '../actions/authentication/logout';
import { createLayout, layoutCreated, layoutCreateFailed } from '../actions/layout/createLayout';
import { deleteLayout, layoutDeleteFailed } from '../actions/layout/deleteLayout';
import LayoutPayload from '../actions/layout/LayoutPayload';
import { layoutSaved, layoutSaveFailed, saveLayout } from '../actions/layout/saveLayout';
import { addPlace, placeAdded, placeAdditionFailed } from '../actions/place/addPlace';
import { enableGuestWeb, enableGuestWebFailed, enableGuestWebFinished } from '../actions/place/enableGuestWeb';
import { enableReservations, enableReservationsFailed, enableReservationsFinished } from '../actions/place/enableReservations';
import { placeUpdated, placeUpdateFailed, updatePlace } from '../actions/place/updatePlace';
import { accountActivationFailed, accountActivationFinished, activateAccount } from '../actions/registration/activateAccount';
import { activatePlace, placeActivationFailed, placeActivationFinished } from '../actions/registration/activatePlace';
import { registerCompany, registrationCompleted, registrationFailed } from '../actions/registration/registerCompany';
import { acceptReservation, reservationAccepted, reservationAcceptFailed } from '../actions/reservation/acceptReservation';
import { cancelReservation, reservationCanceled, reservationCancelFailed } from '../actions/reservation/cancelReservation';
import { createReservation, reservationCreated, reservationCreationFailed } from '../actions/reservation/createReservation';
import { guestArrived, guestArrivedFailed, guestArrivedSaved } from '../actions/reservation/guestArrived';
import { rejectReservation, reservationRejected, reservationRejectFailed } from '../actions/reservation/rejectReservation';
import ReservationPayload from '../actions/reservation/ReservationPayload';
import { reservationResolved, reservationResolveFailed, resolveReservation } from '../actions/reservation/resolveReservation';
import { reservationUpdated, reservationUpdateFailed, updateReservation } from '../actions/reservation/updateReservation';
import { updateReservationGuest } from '../actions/reservation/updateReservationGuest';
import { addSpecialHour, specialHourAdded, specialHourAddFailed } from '../actions/setting/addSpecialHour';
import { deleteSpecialHour, specialHourDeleted, specialHourDeleteFailed } from '../actions/setting/deleteSpecialHour';
import { facebookPageSaved, facebookPageSaveFailed, saveFacebookPage } from '../actions/setting/facebook/saveFacebookPage';
import { modifySpecialHour, specialHourModified, specialHourModifyFailed } from '../actions/setting/modifySpecialHour';
import { contactSaved, contactSaveFailed, saveContact } from '../actions/setting/saveContact';
import { profileSaved, profileSaveFailed, saveProfile } from '../actions/setting/saveProfile';
import { reservationSettingSaved, reservationSettingSaveFailed, saveReservationSetting } from '../actions/setting/saveReservationSetting';
import SpecialHourAction from '../actions/setting/SpecialHourAction';
import { cancelSubscription, subscriptionCanceled, subscriptionCancelFailed } from '../actions/subscription/cancelSubscription';
import { changeSubscriptionPricing, subscriptionPricingChanged, subscriptionPricingChangeFailed } from '../actions/subscription/changeSubscriptionPricing';

interface PlacePostState {
  authenticationPostState: PostState | undefined;
  entityByUuidPostStates: Map<string, PostState>;
  placeStates: Map<string, PostState>;
  facebookPage: Map<string, PostState>;
}

export interface PostStateType extends PlacePostState {
  activatePlacePostState: PostState | undefined;
  emailVerifications: Map<string, PostState>;
  enableReservationsPostState: Map<string, PostState>;
  enableGuestWebPostState: Map<string, PostState>;
  cancelSubscriptionPostState: Map<string, PostState>;
  changeSubscriptionPricingPostState: Map<string, PostState>;
  confirmReservation: Map<string, PostState>;
  accountDeletion: PostState | undefined;
  accountReactivation: PostState | undefined;
}

function getAfterLoginPlacePostState(previous?: PlacePostState): PlacePostState {
  return {
    authenticationPostState: completePost(previous ? previous.authenticationPostState : undefined),
    entityByUuidPostStates: previous ? previous.entityByUuidPostStates : Map<string, PostState>(),
    placeStates: Map<string, PostState>(),
    facebookPage: Map<string, PostState>(),
  };
}

function getAfterLogoutPlacePostState(): PlacePostState {
  return {
    authenticationPostState: undefined,
    entityByUuidPostStates: Map<string, PostState>(),
    placeStates: Map<string, PostState>(),
    facebookPage: Map<string, PostState>(),
  };
}

function postEntity(reducerState: PostStateType, uuid: string): PostStateType {
  const { entityByUuidPostStates } = reducerState;
  return {
    ...reducerState,
    entityByUuidPostStates: entityByUuidPostStates.set(uuid, post(entityByUuidPostStates.get(uuid))),
  };
}

function completePostEntity(reducerState: PostStateType, uuid: string): PostStateType {
  const { entityByUuidPostStates } = reducerState;
  return {
    ...reducerState,
    entityByUuidPostStates: entityByUuidPostStates.set(uuid, completePost(entityByUuidPostStates.get(uuid))),
  };
}

function failPostEntity(reducerState: PostStateType, uuid: string): PostStateType {
  const { entityByUuidPostStates } = reducerState;
  return {
    ...reducerState,
    entityByUuidPostStates: entityByUuidPostStates.set(uuid, failPost(entityByUuidPostStates.get(uuid))),
  };
}

const defaultState: PostStateType = {
  activatePlacePostState: undefined,
  enableReservationsPostState: Map(),
  enableGuestWebPostState: Map(),
  emailVerifications: Map<string, PostState>(),
  cancelSubscriptionPostState: Map(),
  changeSubscriptionPricingPostState: Map(),
  confirmReservation: Map(),
  accountDeletion: undefined,
  accountReactivation: undefined,
  ...getAfterLoginPlacePostState(),
};

const reducer = createReducer<PostStateType>({}, defaultState);

reducer.on(login, (reducerState) => ({
  ...reducerState,
  authenticationPostState: post(reducerState.authenticationPostState),
}));

reducer.on(loginFailed, (reducerState) => ({
  ...reducerState,
  authenticationPostState: failPost(reducerState.authenticationPostState),
}));

reducer.on(authenticationRequired, (reducerState) => ({
  ...reducerState,
  ...getAfterLogoutPlacePostState(),
}));

reducer.on(loginSuccessful, (reducerState) => ({
  ...reducerState,
  ...getAfterLoginPlacePostState(),
}));

reducer.on(logout, (reducerState) => ({
  ...reducerState,
  ...getAfterLogoutPlacePostState(),
}));

reducer.on(registerCompany, (reducerState, { companyRegistrationData }) => {
  return postEntity(reducerState, companyRegistrationData.uuid);
});
reducer.on(registrationCompleted, (reducerState, { uuid }) => {
  return completePostEntity(reducerState, uuid);
});
reducer.on(registrationFailed, (reducerState, { uuid }) => {
  return failPostEntity(reducerState, uuid);
});

reducer.on(activateAccount, (reducerState, { token }) => {
  const { emailVerifications: postStates } = reducerState;
  const emailVerifications = postStates.set(token, post(postStates.get(token)));
  return { ...reducerState, emailVerifications };
});
reducer.on(accountActivationFinished, (reducerState, { token }) => {
  const { emailVerifications: postStates } = reducerState;
  const emailVerifications = postStates.set(token, completePost(postStates.get(token)));
  return { ...reducerState, emailVerifications };
});
reducer.on(accountActivationFailed, (reducerState, { token }) => {
  const { emailVerifications: postStates } = reducerState;
  const emailVerifications = postStates.set(token, failPost(postStates.get(token)));
  return { ...reducerState, emailVerifications };
});

reducer.on(addPlace, (reducerState, { placeUuid }) => {
  const postState = post(reducerState.placeStates.get(placeUuid));
  return {
    ...reducerState,
    placeStates: reducerState.placeStates.set(placeUuid, postState),
  };
});
reducer.on(placeAdded, (reducerState, { placeName }) => {
  const postState = completePost(reducerState.placeStates.get(placeName.uuid));
  return {
    ...reducerState,
    placeStates: reducerState.placeStates.set(placeName.uuid, postState),
  };
});
reducer.on(placeAdditionFailed, (reducerState, { placeName }) => {
  const postState = failPost(reducerState.placeStates.get(placeName.uuid));
  return {
    ...reducerState,
    placeStates: reducerState.placeStates.set(placeName.uuid, postState),
  };
});

reducer.on(updatePlace, (reducerState, { place }) => {
  return postEntity(reducerState, place.uuid);
});
reducer.on(placeUpdated, (reducerState, { place }) => {
  return completePostEntity(reducerState, place.uuid);
});
reducer.on(placeUpdateFailed, (reducerState, { placeUuid }) => {
  return failPostEntity(reducerState, placeUuid);
});

reducer.on(activatePlace, (reducerState) => {
  const { activatePlacePostState } = reducerState;
  const newActivatePlacePostState = post(activatePlacePostState);
  return { ...reducerState, activatePlacePostState: newActivatePlacePostState };
});
reducer.on(placeActivationFinished, (reducerState) => {
  const { activatePlacePostState } = reducerState;
  const newActivatePlacePostState = completePost(activatePlacePostState);
  return { ...reducerState, activatePlacePostState: newActivatePlacePostState };
});
reducer.on(placeActivationFailed, (reducerState) => {
  const { activatePlacePostState } = reducerState;
  const newActivatePlacePostState = failPost(activatePlacePostState);
  return { ...reducerState, activatePlacePostState: newActivatePlacePostState };
});

reducer.on(enableReservations, (reducerState, { token }) => {
  const { enableReservationsPostState } = reducerState;
  const postState = post(enableReservationsPostState.get(token));
  return { ...reducerState, enableReservationsPostState: enableReservationsPostState.set(token, postState) };
});
reducer.on(enableReservationsFinished, (reducerState, { token }) => {
  const { enableReservationsPostState } = reducerState;
  const postState = completePost(enableReservationsPostState.get(token));
  return { ...reducerState, enableReservationsPostState: enableReservationsPostState.set(token, postState) };
});
reducer.on(enableReservationsFailed, (reducerState, { token }) => {
  const { enableReservationsPostState } = reducerState;
  const postState = failPost(enableReservationsPostState.get(token));
  return { ...reducerState, enableReservationsPostState: enableReservationsPostState.set(token, postState) };
});

reducer.on(enableGuestWeb, (reducerState, { token }) => {
  const { enableGuestWebPostState } = reducerState;
  const postState = post(enableGuestWebPostState.get(token));
  return { ...reducerState, enableGuestWebPostState: enableGuestWebPostState.set(token, postState) };
});
reducer.on(enableGuestWebFinished, (reducerState, { token }) => {
  const { enableGuestWebPostState } = reducerState;
  const postState = completePost(enableGuestWebPostState.get(token));
  return { ...reducerState, enableGuestWebPostState: enableGuestWebPostState.set(token, postState) };
});
reducer.on(enableGuestWebFailed, (reducerState, { token }) => {
  const { enableGuestWebPostState } = reducerState;
  const postState = failPost(enableGuestWebPostState.get(token));
  return { ...reducerState, enableGuestWebPostState: enableGuestWebPostState.set(token, postState) };
});

reducer.on(confirmReservation, (reducerState, { token }) => {
  const { confirmReservation } = reducerState;
  const postState = post(confirmReservation.get(token));
  return { ...reducerState, confirmReservation: confirmReservation.set(token, postState) };
});
reducer.on(reservationConfirmationFinished, (reducerState, { token }) => {
  const { confirmReservation } = reducerState;
  const postState = completePost(confirmReservation.get(token));
  return { ...reducerState, confirmReservation: confirmReservation.set(token, postState) };
});
reducer.on(reservationConfirmationFailed, (reducerState, { token }) => {
  const { confirmReservation } = reducerState;
  const postState = failPost(confirmReservation.get(token));
  return { ...reducerState, confirmReservation: confirmReservation.set(token, postState) };
});

reducer.on(saveContact, (reducerState, { contact }) => {
  return postEntity(reducerState, contact.uuid);
});
reducer.on(contactSaved, (reducerState, { contact }) => {
  return completePostEntity(reducerState, contact.uuid);
});
reducer.on(contactSaveFailed, (reducerState, { contactUuid }) => {
  return failPostEntity(reducerState, contactUuid);
});

reducer.on(saveProfile, (reducerState, { profile }) => {
  return postEntity(reducerState, profile.uuid);
});
reducer.on(profileSaved, (reducerState, { profile }) => {
  return completePostEntity(reducerState, profile.uuid);
});
reducer.on(profileSaveFailed, (reducerState, { profileUuid }) => {
  return failPostEntity(reducerState, profileUuid);
});

reducer.on(saveReservationSetting, (reducerState, { reservationSetting }) => {
  return postEntity(reducerState, reservationSetting.uuid);
});
reducer.on(reservationSettingSaved, (reducerState, { reservationSetting }) => {
  return completePostEntity(reducerState, reservationSetting.uuid);
});
reducer.on(reservationSettingSaveFailed, (reducerState, { reservationSettingUuid }) => {
  return failPostEntity(reducerState, reservationSettingUuid);
});

reducer.on(saveFacebookPage, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  const postState = post(facebookPage.get(placeUuid));
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, postState) };
});
reducer.on(facebookPageSaved, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  const postState = completePost(facebookPage.get(placeUuid));
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, postState) };
});
reducer.on(facebookPageSaveFailed, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  const postState = failPost(facebookPage.get(placeUuid));
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, postState) };
});

reducer.on(cancelSubscription, (reducerState, { id }) => {
  const { cancelSubscriptionPostState } = reducerState;
  const postState = post(cancelSubscriptionPostState.get(id));
  return { ...reducerState, cancelSubscriptionPostState: cancelSubscriptionPostState.set(id, postState) };
});
reducer.on(subscriptionCanceled, (reducerState, { subscription }) => {
  const { cancelSubscriptionPostState } = reducerState;
  const postState = completePost(cancelSubscriptionPostState.get(subscription.id));
  return { ...reducerState, cancelSubscriptionPostState: cancelSubscriptionPostState.set(subscription.id, postState) };
});
reducer.on(subscriptionCancelFailed, (reducerState, { id }) => {
  const { cancelSubscriptionPostState } = reducerState;
  const postState = failPost(cancelSubscriptionPostState.get(id));
  return { ...reducerState, cancelSubscriptionPostState: cancelSubscriptionPostState.set(id, postState) };
});

reducer.on(changeSubscriptionPricing, (reducerState, { id }) => {
  const { changeSubscriptionPricingPostState } = reducerState;
  const postState = post(changeSubscriptionPricingPostState.get(id));
  return { ...reducerState, changeSubscriptionPricingPostState: changeSubscriptionPricingPostState.set(id, postState) };
});
reducer.on(subscriptionPricingChanged, (reducerState, { subscription }) => {
  const { changeSubscriptionPricingPostState } = reducerState;
  const postState = completePost(changeSubscriptionPricingPostState.get(subscription.id));
  return { ...reducerState, changeSubscriptionPricingPostState: changeSubscriptionPricingPostState.set(subscription.id, postState) };
});
reducer.on(subscriptionPricingChangeFailed, (reducerState, { id }) => {
  const { changeSubscriptionPricingPostState } = reducerState;
  const postState = failPost(changeSubscriptionPricingPostState.get(id));
  return { ...reducerState, changeSubscriptionPricingPostState: changeSubscriptionPricingPostState.set(id, postState) };
});

reducer.on(createReservation, (reducerState, { pendingReservation }) => {
  return postEntity(reducerState, pendingReservation.uuid);
});
reducer.on(reservationCreated, (reducerState, { reservation }) => {
  return completePostEntity(reducerState, reservation.uuid);
});
reducer.on(reservationCreationFailed, (reducerState, { reservationUuid }) => {
  return failPostEntity(reducerState, reservationUuid);
});

reducer.on(updateReservationGuest, (reducerState, { reservationGuest }) => {
  return postEntity(reducerState, reservationGuest.reservationUuid);
});
reducer.on(updateReservation, (reducerState, { pendingReservation }) => {
  return postEntity(reducerState, pendingReservation.uuid);
});
reducer.on(reservationUpdated, (reducerState, { reservation }) => {
  return completePostEntity(reducerState, reservation.uuid);
});
reducer.on(reservationUpdateFailed, (reducerState, { reservationUuid }) => {
  return failPostEntity(reducerState, reservationUuid);
});

reducer.on(acceptReservation, (reducerState, { pendingReservation }) => {
  return postEntity(reducerState, pendingReservation.uuid);
});
reducer.on(reservationAccepted, (reducerState, { reservation }) => {
  return completePostEntity(reducerState, reservation.uuid);
});
reducer.on(reservationAcceptFailed, (reducerState, { reservationUuid }) => {
  return failPostEntity(reducerState, reservationUuid);
});

reducer.on(resolveReservation, (reducerState, { pendingReservation }) => {
  return postEntity(reducerState, pendingReservation.uuid);
});
reducer.on(reservationResolved, (reducerState, { reservation }) => {
  return completePostEntity(reducerState, reservation.uuid);
});
reducer.on(reservationResolveFailed, (reducerState, { reservationUuid }) => {
  return failPostEntity(reducerState, reservationUuid);
});

function registerActions<T, InitialPayload extends T = T, SuccessPayload extends T = T, FailPayload extends T = T>(
  initialActions: SimpleActionCreator<InitialPayload, object>[],
  successActions: SimpleActionCreator<SuccessPayload, object>[],
  failActions: SimpleActionCreator<FailPayload, object>[],
  getUniqueIdentifier: (payload: T) => string
) {
  initialActions.map((initialAction) => reducer.on(initialAction, (reducerState, payload) => postEntity(reducerState, getUniqueIdentifier(payload))));
  successActions.map((successAction) => reducer.on(successAction, (reducerState, payload) => completePostEntity(reducerState, getUniqueIdentifier(payload))));
  failActions.map((failAction) => reducer.on(failAction, (reducerState, payload) => failPostEntity(reducerState, getUniqueIdentifier(payload))));
}

registerActions<SpecialHourAction>(
  [addSpecialHour, modifySpecialHour, deleteSpecialHour],
  [specialHourAdded, specialHourModified, specialHourDeleted],
  [specialHourAddFailed, specialHourModifyFailed, specialHourDeleteFailed],
  (payload) => payload.specialHourUuid
);

registerActions<ReservationPayload>(
  [acceptReservation, cancelReservation, createReservation, guestArrived, rejectReservation, resolveReservation, updateReservation, updateReservationGuest],
  [reservationAccepted, reservationCanceled, reservationCreated, guestArrivedSaved, reservationRejected, reservationResolved, reservationUpdated],
  [
    reservationAcceptFailed,
    reservationCancelFailed,
    reservationCreationFailed,
    guestArrivedFailed,
    reservationRejectFailed,
    reservationResolveFailed,
    reservationUpdateFailed,
  ],
  (payload) => payload.reservationUuid
);

registerActions<LayoutPayload>(
  [createLayout, saveLayout, deleteLayout],
  [layoutCreated, layoutSaved, layoutDeleteFailed],
  [layoutCreateFailed, layoutSaveFailed, layoutDeleteFailed],
  (payload) => payload.layoutUuid
);

reducer.on(deleteAccount, (reducerState) => ({ ...reducerState, accountDeletion: post(reducerState.accountDeletion) }));
reducer.on(accountDeleted, (reducerState) => ({ ...reducerState, accountDeletion: completePost(reducerState.accountDeletion) }));
reducer.on(accountDeletionFailed, (reducerState) => ({ ...reducerState, accountDeletion: failPost(reducerState.accountDeletion) }));

reducer.on(reactivateAccount, (reducerState) => ({ ...reducerState, accountReactivation: post(reducerState.accountReactivation) }));
reducer.on(accountReactivated, (reducerState) => ({ ...reducerState, accountReactivation: completePost(reducerState.accountReactivation) }));
reducer.on(accountReactivationFailed, (reducerState) => ({ ...reducerState, accountReactivation: failPost(reducerState.accountReactivation) }));

export default reducer;
